<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Has this payee directory grown wrong - a typo, a duplicate, a name I don't
             recognise?"
  Answer:   the name column itself - the one thing worth scanning. There is no rename,
            alias or merge control (the contract exposes `merchants_list` only), so this
            screen answers "is anything wrong" without yet offering "fix it here".
  Context:  when each merchant was first seen - regular weight, muted, matching every
            other list screen's date column.
  Controls: the search box is the one control above the fold; "Load more" is a neutral,
            secondary action, never the page's one --color-action control.
-->
<script setup lang="ts">
import { computed } from 'vue'
import { ErrorState, PageHeader, PageShell } from '@taler/ui'
import { useI18n } from 'vue-i18n'
import {
  canManageMerchants,
  canViewMerchants,
  MerchantList,
  useMerchantActions,
  useMerchants,
} from '@/features/merchants'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()

const canView = computed(() => canViewMerchants(session.can))
const canManage = computed(() => canManageMerchants(session.can))

const merchants = useMerchants({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
})

const actions = useMerchantActions({
  ledgerId: () => session.activeLedger.value?.id,
})

function onSearchInput(event: Event): void {
  merchants.setSearch((event.target as HTMLInputElement).value)
}
</script>

<template>
  <PageShell class="gap-[var(--space-section)]">
    <PageHeader :title="t('merchants.title')">
      <template #actions>
        <input
          type="search"
          :value="merchants.search.value"
          :aria-label="t('merchants.searchLabel')"
          :placeholder="t('merchants.searchPlaceholder')"
          class="min-h-[var(--density-control)] w-64 rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
          @input="onSearchInput"
        />
      </template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('merchants.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('merchants.noAccess') }}
    </p>

    <template v-else>
      <p v-if="merchants.isLoading.value" role="status">{{ t('app.loading') }}</p>

      <ErrorState
        v-else-if="merchants.error.value !== null"
        :title="t('merchants.loadError')"
        :retry-label="t('merchants.retry')"
        :retrying="merchants.retrying.value"
        @retry="merchants.retry()"
      />

      <p v-else-if="merchants.merchants.value.length === 0" class="text-[color:var(--color-text-muted)]">
        {{ merchants.search.value === '' ? t('merchants.emptyState') : t('merchants.noResults') }}
      </p>

      <MerchantList
        v-else
        :merchants="merchants.merchants.value"
        :has-next-page="merchants.hasNextPage.value"
        :is-fetching-next-page="merchants.isFetchingNextPage.value"
        :can-manage="canManage"
        :add-alias="actions.addAlias"
        :remove-alias="actions.removeAlias"
        :count-entries="actions.countEntries"
        :merge="actions.merge"
        @fetch-next-page="merchants.fetchNextPage()"
      />
    </template>
  </PageShell>
</template>
